import isUrl from 'is-url'
import { Editor, type Node, Range, Element as SlateElement, Transforms } from 'slate'

import type { UserMention } from '../types'

// This is all adapted from
// https://github.com/ianstormtaylor/slate/blob/main/site/examples/inlines.tsx

function isLinkNode(n: Node): n is SlateElement & { type: 'link' } {
  return SlateElement.isElement(n) && n.type === 'link'
}

export function getActiveLinkUrl(editor: Editor): string | null {
  const { selection } = editor
  if (selection == null) {
    return null
  }
  const [link] = Editor.nodes(editor, {
    at: Editor.unhangRange(editor, selection),
    match: isLinkNode,
  })
  if (link == null) {
    return null
  }
  const [linkNode] = link
  return isLinkNode(linkNode) ? linkNode.url : null
}

export function isLinkActive(editor: Editor): boolean {
  return getActiveLinkUrl(editor) != null
}

export function unwrapLink(editor: Editor): void {
  Transforms.unwrapNodes(editor, {
    match: isLinkNode,
  })
}

function wrapLink(editor: Editor, url: string): void {
  if (isLinkActive(editor)) {
    Transforms.setNodes(
      editor,
      {
        url,
      },
      { match: isLinkNode }
    )
    return
  }

  const { selection } = editor
  const isCollapsed = selection != null && Range.isCollapsed(selection)
  const link: SlateElement = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: url }] : [],
  }

  if (isCollapsed) {
    Transforms.insertNodes(editor, link)
  } else {
    Transforms.wrapNodes(editor, link, { split: true })
    Transforms.collapse(editor, { edge: 'end' })
  }
}

export function insertLink(editor: Editor, url: string): void {
  const { selection } = editor
  if (selection != null) {
    wrapLink(editor, url)
  }
}

export function insertMention(editor: Editor, user: UserMention): void {
  const mention: SlateElement = {
    type: 'mention',
    displayName: user.displayName,
    userId: user.id,
    username: user.username,
    children: [{ text: '' }],
  }
  Transforms.insertNodes(editor, mention)
  Transforms.move(editor)
}

export function withInlines(editor: Editor): Editor {
  const { insertData, insertText, isInline, isVoid } = editor

  // eslint-disable-next-line no-param-reassign
  editor.isInline = element =>
    ['link', 'image', 'math', 'mention'].includes(element.type) || isInline(element)

  // eslint-disable-next-line no-param-reassign
  editor.isVoid = element => element.type === 'mention' || isVoid(element)

  // eslint-disable-next-line no-param-reassign
  editor.insertText = text => {
    if (text && isUrl(text)) {
      wrapLink(editor, text)
    } else {
      insertText(text)
    }
  }

  // eslint-disable-next-line no-param-reassign
  editor.insertData = data => {
    const text = data.getData('text/plain')
    if (text && isUrl(text)) {
      wrapLink(editor, text)
    } else {
      insertData(data)
    }
  }

  return editor
}
